import { useRef, useState } from 'react';
import { Loader2, MailCheck, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TextField, PasswordField } from '../components/fields';
import { requestMagicLink, passwordLogin } from '../lib/auth';

type Mode = 'magic' | 'password';
type Status = 'idle' | 'submitting' | 'sent' | 'error';

/**
 * Spec 003 — sign in (SC-010/014/015/019). Magic link is primary; "use a password instead" reveals the
 * fallback. Four states: idle → submitting (disabled + spinner) → sent (neutral "check your email"
 * panel, enumeration-safe — identical whether or not an account exists) OR error (specific message).
 * Password login on success navigates into the account area. WCAG 2.1 AA throughout.
 */
export default function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('magic');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const sentRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);

  function resetMessages() {
    setFieldErrors({});
    setGeneralError(null);
  }

  async function submitMagic(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'submitting') return;
    resetMessages();
    setStatus('submitting');
    const result = await requestMagicLink(email);
    if (result.kind === 'ok') {
      setStatus('sent');
      queueMicrotask(() => sentRef.current?.focus());
      return;
    }
    if (result.kind === 'invalid') {
      setFieldErrors(result.errors);
      setStatus('error');
      return;
    }
    setGeneralError(
      result.kind === 'rate_limited'
        ? 'Too many attempts. Please wait a little while and try again.'
        : 'We couldn’t reach the server. Please check your connection and try again.',
    );
    setStatus('error');
    queueMicrotask(() => errorRef.current?.focus());
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'submitting') return;
    resetMessages();
    setStatus('submitting');
    const result = await passwordLogin(email, password);
    if (result.kind === 'ok') {
      navigate('/account');
      return;
    }
    if (result.kind === 'invalid') {
      setFieldErrors(result.errors);
      setStatus('error');
      return;
    }
    const message =
      result.kind === 'rejected' || result.kind === 'locked'
        ? result.message
        : result.kind === 'rate_limited'
          ? 'Too many attempts. Please wait a little while and try again.'
          : 'We couldn’t reach the server. Please check your connection and try again.';
    setGeneralError(message);
    setStatus('error');
    queueMicrotask(() => errorRef.current?.focus());
  }

  const busy = status === 'submitting';

  return (
    <div className="mx-auto w-full max-w-md px-6 py-16 sm:py-24">
      <p className="font-display text-xs font-semibold uppercase tracking-[0.28em] text-accent-deep">
        Members
      </p>
      <h1 className="mt-3 font-display text-3xl font-semibold uppercase tracking-[0.12em] text-ink sm:text-4xl">
        Sign in
      </h1>

      {status === 'sent' ? (
        <div
          ref={sentRef}
          tabIndex={-1}
          className="mt-8 rounded-lg border border-border bg-surface p-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <MailCheck aria-hidden className="h-7 w-7 text-success" />
          <h2 className="mt-3 font-display text-lg font-semibold uppercase tracking-[0.1em] text-ink">
            Check your email
          </h2>
          <p className="mt-2 font-serif text-base leading-relaxed text-ink">
            If an account exists for that address, we’ve sent a one-time sign-in link. It works once and
            expires in 15 minutes.
          </p>
          <button
            type="button"
            onClick={() => {
              setStatus('idle');
              setPassword('');
            }}
            className="mt-4 font-sans text-sm text-accent underline decoration-accent underline-offset-4 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Use a different email
          </button>
        </div>
      ) : (
        <>
          <p className="mt-4 font-serif text-lg leading-relaxed text-ink">
            {mode === 'magic'
              ? 'Enter your email and we’ll send you a one-time sign-in link. No password needed.'
              : 'Sign in with your email and password.'}
          </p>

          {generalError && (
            <p
              ref={errorRef}
              tabIndex={-1}
              role="alert"
              className="mt-6 flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error"
            >
              <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
              {generalError}
            </p>
          )}

          <form
            onSubmit={mode === 'magic' ? submitMagic : submitPassword}
            noValidate
            className="mt-6 space-y-5"
          >
            <TextField
              id="login-email"
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
              error={fieldErrors.email}
              required
              autoFocus
              disabled={busy}
            />
            {mode === 'password' && (
              <PasswordField
                id="login-password"
                label="Password"
                autoComplete="current-password"
                value={password}
                onChange={setPassword}
                error={fieldErrors.password}
                disabled={busy}
              />
            )}

            <button
              type="submit"
              disabled={busy}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-accent px-4 py-2.5 font-sans text-sm font-semibold text-bg transition-colors duration-150 ease-standard hover:bg-accent-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
            >
              {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
              {mode === 'magic' ? 'Email me a sign-in link' : 'Sign in'}
            </button>
          </form>

          <button
            type="button"
            onClick={() => {
              setMode((m) => (m === 'magic' ? 'password' : 'magic'));
              resetMessages();
              setStatus('idle');
            }}
            className="mt-5 font-sans text-sm text-accent underline decoration-accent underline-offset-4 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {mode === 'magic' ? 'Use a password instead' : 'Email me a link instead'}
          </button>

          <p className="sr-only" aria-live="polite">
            {busy ? 'Signing in.' : ''}
          </p>
        </>
      )}
    </div>
  );
}
